import { useEffect, useState } from "react"

import {
  AiAnalysisTopicData,
  PreviewAiAnalysisAnalysedOn,
  PreviewAiAnalysisMessageItem,
  PreviewAiAnalysisMessagesData,
  PreviewAiAnalysisPromptTurn,
  TestAiAnalysisData,
  previewAiAnalysisMessagesQuery,
  testAiAnalysisMutation
} from "@domain/ai_analysis_test"
import { useCustomersQuery } from "@hooks/use_customers"
import useTranslate from "@hooks/use_translate"
import CancelIcon from "@mui/icons-material/Cancel"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import PlayCircleIcon from "@mui/icons-material/PlayCircle"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import TroubleshootIcon from "@mui/icons-material/Troubleshoot"
import VisibilityIcon from "@mui/icons-material/Visibility"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListSubheader,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { showErrorToast } from "@utils/toast"

const ANALYSED_ON_OPTIONS: {
  value: PreviewAiAnalysisAnalysedOn
  label: string
  group: "Conversation" | "Ticket"
}[] = [
  {
    value: "OPEN",
    label: "OPEN — sohbet açıldı / ilk mesaj",
    group: "Conversation"
  },
  {
    value: "OPEN_REOPEN",
    label: "OPEN_REOPEN — sohbet yeniden açıldı",
    group: "Conversation"
  },
  { value: "CLOSED", label: "CLOSED — sohbet kapandı", group: "Conversation" },
  { value: "CREATED", label: "CREATED — talep oluşturuldu", group: "Ticket" },
  {
    value: "CREATED_REOPENED",
    label: "CREATED_REOPENED — talep yeniden açıldı",
    group: "Ticket"
  },
  { value: "RESOLVED", label: "RESOLVED — talep çözüldü", group: "Ticket" }
]

function sentimentColor(type?: string): "success" | "error" | "default" {
  if (type === "POSITIVE") return "success"
  if (type === "NEGATIVE") return "error"
  return "default"
}

function StatRow({
  label,
  value,
  good
}: {
  label: string
  value: React.ReactNode
  good?: boolean
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 0.5 }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {typeof good === "boolean" ? (
        <Chip
          icon={good ? <CheckCircleIcon /> : <CancelIcon />}
          label={value}
          color={good ? "success" : "default"}
          size="small"
          variant={good ? "filled" : "outlined"}
        />
      ) : (
        <Typography variant="body2" fontWeight={600}>
          {value}
        </Typography>
      )}
    </Stack>
  )
}

function PromptTurnBubble({ turn }: { turn: PreviewAiAnalysisPromptTurn }) {
  const isAssistant = turn.role === "assistant"
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isAssistant ? "flex-end" : "flex-start"
      }}
    >
      <Box
        sx={{
          maxWidth: "80%",
          p: 1.5,
          borderRadius: 2,
          bgcolor: isAssistant ? "primary.main" : "grey.200",
          color: isAssistant ? "primary.contrastText" : "text.primary"
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
          {isAssistant ? "🤖 asistan / işletme" : "🧑 müşteri"}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {turn.content}
        </Typography>
      </Box>
    </Box>
  )
}

function SelectedMessageRow({ item }: { item: PreviewAiAnalysisMessageItem }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Typography
        variant="caption"
        sx={{ minWidth: 130, color: "text.secondary" }}
      >
        {item.createdAt
          ? new Date(item.createdAt).toLocaleString("tr-TR")
          : "-"}
      </Typography>
      <Chip
        label={item.senderType === "Persons" ? "müşteri" : "temsilci/AI"}
        size="small"
        variant="outlined"
      />
      <Chip label={item.messageType || "-"} size="small" variant="outlined" />
      {item.isCommentOrQuestion && (
        <Chip
          label={item.hasReply ? "yanıtlanmış" : "yanıtsız"}
          size="small"
          color={item.hasReply ? "success" : "warning"}
        />
      )}
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {item.contentPreview}
      </Typography>
    </Stack>
  )
}

export default function AiAnalysisTestPage() {
  const { translate } = useTranslate()
  const { customerOptions, isLoading: customersLoading } =
    useCustomersQuery(true)

  const [customerId, setCustomerId] = useState("")
  const [conversationId, setConversationId] = useState("")
  const [analyseOn, setAnalyseOn] =
    useState<PreviewAiAnalysisAnalysedOn>("CLOSED")

  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewData, setPreviewData] =
    useState<PreviewAiAnalysisMessagesData | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewTab, setPreviewTab] = useState<"messages" | "prompt">(
    "messages"
  )

  const [testLoading, setTestLoading] = useState(false)
  const [testData, setTestData] = useState<TestAiAnalysisData | null>(null)
  const [testError, setTestError] = useState<string | null>(null)

  const canSubmit = Boolean(customerId && conversationId && analyseOn)
  const isFirstMessageTrigger =
    analyseOn === "OPEN" || analyseOn === "OPEN_REOPEN"

  // Stale results from a previous conversation must never be shown against the
  // current form selection — reset every result the moment any input changes.
  useEffect(() => {
    setPreviewData(null)
    setPreviewError(null)
    setTestData(null)
    setTestError(null)
  }, [customerId, conversationId, analyseOn])

  function handleReset() {
    setCustomerId("")
    setConversationId("")
    setAnalyseOn("CLOSED")
  }

  async function handlePreview() {
    setPreviewLoading(true)
    setPreviewError(null)
    setTestData(null)
    setTestError(null)

    const response = await previewAiAnalysisMessagesQuery({
      customerId,
      conversationId,
      analyseOn
    })

    if (!response.isSuccess || !response.data) {
      const message = response.errorMessage || "Önizleme başarısız oldu"
      setPreviewError(message)
      showErrorToast(message)
      setPreviewLoading(false)
      return
    }

    setPreviewData(response.data)
    setPreviewTab("messages")
    setPreviewLoading(false)
  }

  async function handleTestAnalysis() {
    setTestLoading(true)
    setTestError(null)

    const response = await testAiAnalysisMutation({
      customerId,
      conversationId,
      analyseOn
    })

    if (!response.isSuccess || !response.data) {
      const message = response.errorMessage || "Test analizi başarısız oldu"
      setTestError(message)
      showErrorToast(message)
      setTestLoading(false)
      return
    }

    setTestData(response.data)
    setTestLoading(false)
  }

  function getNotAllowedReason(
    data: PreviewAiAnalysisMessagesData | null
  ): string | null {
    if (!data) {
      return null
    }
    if (!data.isAllowedToAnalyze) {
      return "AI Analiz ayarlarında bu tetikleme noktası (analysedOn) için analiz kapalı."
    }
    if (data.topicsCount < 5) {
      return `Bu müşteride en az 5 konu (topic) tanımlı olmalı, şu an ${data.topicsCount} tane var.`
    }
    return null
  }

  const notAllowedReason = getNotAllowedReason(previewData)

  return (
    <Box sx={{ p: 3, maxWidth: 1100, margin: "0 auto" }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <TroubleshootIcon color="primary" />
        <Typography variant="h5">AI Analiz Test Aracı</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Gerçek bir konuşmayı, gerçek analiz motorunun (
        <code>AiAnalysisService</code>) aynısından geçirir. Hiçbir gerçek veri
        yazılmaz veya değiştirilmez — hem önizleme hem de test çalıştırma
        tamamen güvenlidir.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subtitle1" fontWeight={600}>
              1. Konuşmayı seç
            </Typography>
            <Tooltip title="Formu temizle">
              <IconButton size="small" onClick={handleReset}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack gap={2} sx={{ mt: 1 }}>
            <Autocomplete<{ id: string; label: string }>
              options={customerOptions}
              disabled={customersLoading}
              value={customerOptions.find((o) => o.id === customerId) ?? null}
              onChange={(_, option) => setCustomerId(option?.id ?? "")}
              getOptionLabel={(o) => `${o.label} (${o.id})`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate("customer")}
                  placeholder="Müşteri adı veya ID ile arayın…"
                />
              )}
              noOptionsText="Müşteri bulunamadı"
              loadingText="Yükleniyor…"
              loading={customersLoading}
            />

            <TextField
              label="Conversation ID"
              placeholder="Analiz edilecek konuşmanın Mongo _id'sini girin"
              value={conversationId}
              onChange={(e) => setConversationId(e.target.value.trim())}
              fullWidth
            />

            <TextField
              select
              label="Analiz Tetikleme Noktası (analysedOn)"
              value={analyseOn}
              onChange={(e) =>
                setAnalyseOn(e.target.value as PreviewAiAnalysisAnalysedOn)
              }
              helperText="Analizin hangi olayda tetiklendiğini simüle eder — gerçekte hangi olay yaşandıysa onu seçin"
              fullWidth
            >
              {(["Conversation", "Ticket"] as const).flatMap((group) => [
                <ListSubheader key={group}>{group}</ListSubheader>,
                ...ANALYSED_ON_OPTIONS.filter((o) => o.group === group).map(
                  (option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )
                )
              ])}
            </TextField>

            <Divider />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    previewLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <VisibilityIcon />
                    )
                  }
                  onClick={handlePreview}
                  disabled={!canSubmit || previewLoading}
                >
                  {previewLoading ? "Önizleniyor…" : "2. Mesajları Önizle"}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Ücretsiz — LLM çağrısı yapmaz, hiçbir şey yazmaz. Hangi
                  mesajların seçileceğini gösterir.
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={
                    testLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PlayCircleIcon />
                    )
                  }
                  onClick={handleTestAnalysis}
                  disabled={!canSubmit || testLoading}
                >
                  {testLoading ? "Çalışıyor…" : "3. Test Analizini Çalıştır"}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Gerçek bir LLM çağrısı yapar (ücretli olabilir). Sonucu hiçbir
                  yere kaydetmez — tamamen ayrı, güvenli bir çalıştırma.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {!previewData && !testData && !previewLoading && !testLoading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Yukarıdaki formu doldurup önce <strong>Mesajları Önizle</strong> ile
          başlayın — hangi mesajların analize gireceğini görün, sonra isterseniz{" "}
          <strong>Test Analizini Çalıştır</strong>&apos;a geçin.
        </Alert>
      )}

      {previewError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setPreviewError(null)}
        >
          {previewError}
        </Alert>
      )}

      {previewData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              2. Önizleme Sonucu
            </Typography>

            <Alert
              severity={previewData.willRunInProduction ? "success" : "warning"}
              icon={
                previewData.willRunInProduction ? (
                  <CheckCircleIcon />
                ) : (
                  <WarningAmberIcon />
                )
              }
              sx={{ mb: 2 }}
            >
              <AlertTitle>
                {previewData.willRunInProduction
                  ? "Bu haliyle gerçek sistemde analiz çalışır"
                  : "Bu haliyle gerçek sistemde analiz ÇALIŞMAZ"}
              </AlertTitle>
              {notAllowedReason ||
                "Ayarlar ve konu sayısı analiz için yeterli."}
            </Alert>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)"
                },
                columnGap: 4,
                mb: 2
              }}
            >
              <StatRow
                label="Tür"
                value={previewData.isTicket ? "Talep (Ticket)" : "Sohbet"}
              />
              <StatRow label="Kanal" value={previewData.channelType || "-"} />
              <StatRow label="Konu sayısı" value={previewData.topicsCount} />
              <StatRow
                label="Ayar açık mı?"
                value={previewData.isAllowedToAnalyze ? "evet" : "hayır"}
                good={previewData.isAllowedToAnalyze}
              />
              <StatRow
                label="Kullanılacak prompt"
                value={previewData.promptName}
              />
            </Box>

            <Divider sx={{ mb: 1 }} />

            <Tabs
              value={previewTab}
              onChange={(_, value: "messages" | "prompt") =>
                setPreviewTab(value)
              }
              sx={{ mb: 2 }}
            >
              <Tab
                value="messages"
                label={`Seçilen Mesajlar (${previewData.selectedMessages.length})`}
              />
              <Tab
                value="prompt"
                label={`LLM'e Giden Prompt (${previewData.formattedPrompt.length})`}
              />
            </Tabs>

            {previewTab === "messages" && (
              <>
                {isFirstMessageTrigger && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <code>{analyseOn}</code> bir ilk-mesaj tetiklemesi olduğu
                    için, aşağıdaki mesajda &quot;yanıtlanmış&quot; görünse bile
                    o yanıt LLM&apos;e <strong>bilerek gönderilmez</strong> — bu
                    tetikleme noktası müşterinin cevaptan ÖNCEKİ ilk niyetini
                    yakalamak içindir.
                  </Alert>
                )}
                {previewData.selectedMessages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Bu tetikleme noktası için hiçbir mesaj seçilmedi.
                  </Typography>
                ) : (
                  <Box>
                    {previewData.selectedMessages.map((item, index) => (
                      <SelectedMessageRow key={item._id || index} item={item} />
                    ))}
                  </Box>
                )}
              </>
            )}

            {previewTab === "prompt" && (
              <Stack gap={1}>
                <Typography variant="caption" color="text.secondary">
                  Bu, mesajların LLM&apos;e tam olarak nasıl gönderileceğidir —
                  yukarıdaki mesajlarla birebir aynı olması gerekmez (context
                  bilgisi, kırpma ve{" "}
                  {isFirstMessageTrigger
                    ? "ilk-mesaj tetiklemelerinde cevapların bilerek hariç tutulması"
                    : "yanıt eşleme"}{" "}
                  burada uygulanmış olur).
                </Typography>
                {previewData.formattedPrompt.map((turn, index) => (
                  <PromptTurnBubble key={index} turn={turn} />
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {testError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setTestError(null)}
        >
          {testError}
        </Alert>
      )}

      {testData && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              3. Test Analiz Sonucu
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip
                label={testData.sentiment.type}
                color={sentimentColor(testData.sentiment.type)}
              />
              <Chip
                label={`güven: ${testData.sentiment.confidence}`}
                variant="outlined"
              />
              {testData.topics.map((topic: AiAnalysisTopicData) => (
                <Chip
                  key={topic.name}
                  label={`${topic.label} (${topic.confidence})`}
                  variant="outlined"
                />
              ))}
            </Stack>

            <Typography variant="body1" sx={{ mb: 2 }}>
              {testData.summary}
            </Typography>

            {testData.reasoning && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Modelin gerekçesi (reasoning)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                  >
                    {testData.reasoning}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary">
              model: {testData.model} · prompt: {testData.promptName} · kanal:{" "}
              {testData.channelLabel}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
